import {useEffect, useState} from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentServicemodulesQueryKey,
  usePatchApiCompaniesCurrentServicemodulesAbsenceServiceModuleId,
  usePutApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdCalendarCalendarServiceModuleId,
  useDeleteApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdCalendar,
  useGetApiCompaniesCurrentServicemodules,
} from '@/api/generated/company-management-user/company-management-user'
import {type AbsenceServiceModuleModel, type ServiceModuleModel, ServiceModuleType} from '@/api/model'
import { notify } from '@/lib/notify'
import { useStandardServiceModuleFields } from '../useStandardServiceModuleFields'
import { StandardModuleFields } from '../EditServiceModuleFields'
import { EditServiceModuleDialog } from '../EditServiceModuleDialog'
import { Label } from '@/components/shadcn/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select'
import { X } from 'lucide-react'
import {Button} from "@/components/shadcn/button.tsx";

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

export function EditAbsenceServiceModule({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-absence', 'service-modules', 'common'])
  const standardFields = useStandardServiceModuleFields(module)
  const queryClient = useQueryClient()

  const detail = module as AbsenceServiceModuleModel

  const { data: calendarModulesData, isLoading: calendarModulesLoading } =
    useGetApiCompaniesCurrentServicemodules({ type: ServiceModuleType.Calendar })

  const calendars = ((calendarModulesData as any)?.data ?? calendarModulesData) as
    | ServiceModuleModel[]
    | undefined

  const patch = usePatchApiCompaniesCurrentServicemodulesAbsenceServiceModuleId()
  const assignCalendar =
    usePutApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdCalendarCalendarServiceModuleId()
  const removeCalendar =
    useDeleteApiCompaniesCurrentServicemodulesAbsenceServiceModuleIdCalendar()

  const [selectedCalendarId, setSelectedCalendarId] = useState<number | null>(
    (detail.calendarServiceModuleId as number | null) ?? null
  )
  useEffect(() => {
    setSelectedCalendarId((detail.calendarServiceModuleId as number | null) ?? null)
  }, [detail.calendarServiceModuleId])

  async function handleSave() {
    try {
      await patch.mutateAsync({
        serviceModuleId: module.id,
        data: standardFields.buildBasePayload(),
      })
      await standardFields.saveTranslations()

      if (selectedCalendarId !== (detail.calendarServiceModuleId ?? null)) {
        if (selectedCalendarId != null) {
          await assignCalendar.mutateAsync({
            serviceModuleId: module.id,
            calendarServiceModuleId: selectedCalendarId,
          })
        } else {
          await removeCalendar.mutateAsync({ serviceModuleId: module.id })
        }
      }

      await queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentServicemodulesQueryKey(),
      })
      notify.success(t('toasts.saved', { ns: 'service-modules' }))
      onClose()
    } catch (error) {
      notify.apiError(error, t('toasts.saveError', { ns: 'service-modules' }))
    }
  }

  const specific = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label>{t('editAbsence.calendarLabel')}</Label>
        <div className="flex gap-2">
          <Select
            value={selectedCalendarId?.toString() ?? 'none'}
            onValueChange={(val) =>
              setSelectedCalendarId(val === 'none' ? null : Number(val))
            }
            disabled={calendarModulesLoading}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder={t('editAbsence.calendarPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">
                {t('editAbsence.noCalendar')}
              </SelectItem>
              {calendars?.map((cal) => (
                <SelectItem key={cal.id} value={cal.id.toString()}>
                  {cal.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCalendarId != null && (
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSelectedCalendarId(null)}
            >
              <X className="size-4 text-destructive" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )

  const isSaving =
    patch.isPending || assignCalendar.isPending || removeCalendar.isPending

  return (
    <EditServiceModuleDialog
      id={module.id}
      moduleName={module.name}
      dialogTitle={t('editAbsence.title')}
      onClose={onClose}
      standard={<StandardModuleFields fields={standardFields} moduleType={module.type} />}
      specific={specific}
      onSave={handleSave}
      isSaving={isSaving}
    />
  )
}
